import { GetImpactRankingsByYearsQuery } from '#types/generated/graphql';
import { gql, useQuery } from '@apollo/client';
import { useMemo } from 'react';

export const getImpactRankingsByYears = gql`
  query GetImpactRankingsByYears(
    $years: [Int]
    $institutionId: String
    $level1Id: String
    $releaseNames: [String]!
    $responseFields: [ResponseFields2ListItem]
  ) {
    releases(
      years: $years
      institutionId: $institutionId
      level1Id: $level1Id
      releaseNames: $releaseNames
      responseFields: $responseFields
    ) {
      id
      year
      level1 {
        id
        result {
          jointRanked
          rank {
            lower
            higher
          }
        }
      }
    }
  }
`;

/**
 * Returns the overall Impact ranking for the most recent year, out of the years
 * the user is allowed to see, that the institution is actually ranked in.
 *
 * The available years already account for the Impact release/embargo dates of
 * the user's Impact product, so this is deliberately independent of any WUR
 * permissions or of the year selected for WUR.
 */
export const useLatestImpactRanking = (
  institutionId: string,
  availableImpactYears: number[],
) => {
  const { data } = useQuery<GetImpactRankingsByYearsQuery>(
    getImpactRankingsByYears,
    {
      variables: {
        years: availableImpactYears,
        institutionId,
        level1Id: 'overall',
        releaseNames: ['impact'],
        responseFields: ['LEVEL1S'],
      },
      skip: !institutionId || availableImpactYears.length === 0,
    },
  );

  return useMemo(() => {
    const [latestRankedRelease] = (data?.releases ?? [])
      .filter((release) => release?.level1?.result?.rank)
      .sort((a, b) => b.year - a.year);

    return {
      year: latestRankedRelease?.year,
      rank: latestRankedRelease?.level1?.result?.rank,
    };
  }, [data]);
};
